import { Capacitor } from "@capacitor/core";

// Use environment variable for backend host
// - If REACT_APP_BACKEND_HOST is explicitly set, use it
// - Otherwise, use empty string for relative URLs (proxied through nginx)
export const HOST = process.env.REACT_APP_BACKEND_HOST || "";

export const endpoints = {
  whoami: `${HOST}/api/v1/whoami`,
  login: `${HOST}/unprotected/api/v1/login`,
  signup: `${HOST}/api/v1/user/signup`,
  signupWithEmail: `${HOST}/unprotected/api/v1/user/signup`,
  recoverAccount: `${HOST}/unprotected/api/v1/user/recover-account`,
  resetPassword: `${HOST}/unprotected/api/v1/user/reset-password`,
  confirmEmail: `${HOST}/unprotected/api/v1/user/send-confirm-email`,
  confirmedEmail: `${HOST}/unprotected/api/v1/user/confirm-email`,
  meetings: `${HOST}/api/v1/meetings/list`,
  meetingsWithUser: `${HOST}/api/v1/meetings/list/`,
  updateProfile: `${HOST}/api/v1/profile/update`,
  guestIntake: `${HOST}/api/v1/profile/guest-intake`,
  guestConvert: `${HOST}/api/v1/profile/guest-convert`,
  changeLanguage: `${HOST}/api/v1/profile/language`,
  currentProfile: `${HOST}/api/v1/profile/current`,
  getProfile: `${HOST}/api/v1/profile/public/`,
  addSlot: `${HOST}/api/v1/meetings/slot`,
  getSlots: `${HOST}/api/v1/meetings/slots`,
  deleteSlot: `${HOST}/api/v1/meetings/slot/`,
  getAvailableHours: `${HOST}/unprotected/api/v1/meetings/slots/available/`,
  timezones: `${HOST}/api/v1/meetings/timezones`,
  getPublicProfile: `${HOST}/unprotected/api/v1/profile/public/`,
  messagingRecipients: `${HOST}/api/v1/messaging/recipients`,
  getIntakeMentors: `${HOST}/unprotected/api/v1/intake/mentors?limit=30`,
  intakeSpeech: `${HOST}/unprotected/api/v1/intake/speech`,
  intakeSentiment: `${HOST}/unprotected/api/v1/intake/sentiment`,
  intakeVision: `${HOST}/unprotected/api/v1/intake/vision`,
  intakeSessions: `${HOST}/unprotected/api/v1/intake/sessions`,
  intakeSession: (sessionId: string) =>
    `${HOST}/unprotected/api/v1/intake/sessions/${sessionId}`,
  getSearchMentors: `${HOST}/api/v1/search/mentors?limit=30`,
  searchUsers: `${HOST}/api/v1/search/users`,
  getRandomMentors: `${HOST}/api/v1/search/random?limit=3`,
  getSearchFilters: `${HOST}/api/v1/search/filters`,
  ensureMessagingSendbird: `${HOST}/api/v1/messaging/ensure-sendbird`,
  ensureGroupChatManagerAccess: `${HOST}/api/v1/messaging/ensure-manager-access`,
  bookMeeting: `${HOST}/api/v1/meetings/book`,
  cancelMeeting: `${HOST}/api/v1/meetings/cancel`,
  logout: `${HOST}/api/v1/logout`,
  getAvatar: `${HOST}/api/v1/user/avatar/`,
  addAvatar: `${HOST}/api/v1/user/upload-avatar`,
  config: `${HOST}/unprotected/api/v1/config`,
  version: `${HOST}/api/v1/version`,
  versionDetail: (version: string) =>
    `${HOST}/api/v1/version/${encodeURIComponent(version)}`,
  getPost: (postId: number) => `${HOST}/api/v1/feed/${postId}`,
  getPosts: (limit = 10, offset = 0) =>
    `${HOST}/api/v1/feed?limit=${limit}&offset=${offset}`,
  getAllPosts: `${HOST}/api/v1/feed`,
  createPost: `${HOST}/api/v1/feed`,
  updatePost: (postId: number) => `${HOST}/api/v1/feed/${postId}`,
  deletePost: (postId: number) => `${HOST}/api/v1/feed/${postId}`,
  getScheduledPosts: `${HOST}/api/v1/admin/feed/scheduled`,
  cancelScheduledPost: (postId: number) => `${HOST}/api/v1/admin/feed/${postId}/cancel`,
  userModeration: {
    block: (userId: string | number) => `${HOST}/api/v1/users/${userId}/block`,
    deleteContact: (userId: string | number) => `${HOST}/api/v1/users/${userId}/contact`,
    report: (userId: string | number) => `${HOST}/api/v1/users/${userId}/report`,
  },
  getBanner: `${HOST}/api/v1/banner`,
  getEvents: (filter?: 'all' | 'upcoming' | 'past') => 
    filter ? `${HOST}/api/v1/events?filter=${filter}` : `${HOST}/api/v1/events`,
  getEvent: (eventId: number) => `${HOST}/api/v1/events/${eventId}`,
  sms: {
    getPreferences: `${HOST}/api/v1/sms/preferences`,
    updatePreferences: `${HOST}/api/v1/sms/preferences`,
    startVerification: `${HOST}/api/v1/sms/verify/start`,
    checkVerification: `${HOST}/api/v1/sms/verify/check`,
    removePhone: `${HOST}/api/v1/sms/phone`,
  },
  admin: {
    getUsers: `${HOST}/api/v1/admin/users`,
    getUser: `${HOST}/api/v1/admin/users`,
    bookMeeting: `${HOST}/api/v1/admin/meetings`,
    updateMeeting: `${HOST}/api/v1/meetings`,
    deleteMeeting: `${HOST}/api/v1/admin/meetings`,
    createUser: `${HOST}/api/v1/admin/users`,
    deleteUser: `${HOST}/api/v1/admin/users`,
    revokeAdmin: `${HOST}/api/v1/admin/admins`,
    resetPassword: `${HOST}/api/v1/admin/reset-password`,
    uploadBanner: `${HOST}/api/v1/admin/banner/upload`,
    publishBanner: `${HOST}/api/v1/admin/banner/publish`,
    deletePreviewBanner: `${HOST}/api/v1/admin/banner/cancel`,
    deleteBanner: `${HOST}/api/v1/admin/banner/delete`,
    getStatistics: `${HOST}/api/v1/admin/statistics`,
    getUserActions: `${HOST}/api/v1/admin/user-actions`,
    getModerationCSV: `${HOST}/api/v1/admin/user-actions/csv`,
    getCSVReport: `${HOST}/api/v1/admin/csvreport`,
    getUserDetailCSV: `${HOST}/api/v1/admin/user-detail/csv`,
    getQRAnalyticsCSV: `${HOST}/api/v1/admin/qr-analytics/csv`,
    getMeetings: `${HOST}/api/v1/meetings/`,
    importUsers: `${HOST}/api/v1/admin/import`,
    createEvent: `${HOST}/api/v1/admin/events`,
    updateEvent: (eventId: number) => `${HOST}/api/v1/admin/events/${eventId}`,
    deleteEvent: (eventId: number) => `${HOST}/api/v1/admin/events/${eventId}`,
    publishEvent: (eventId: number) => `${HOST}/api/v1/admin/events/${eventId}/publish`,
    unpublishEvent: (eventId: number) => `${HOST}/api/v1/admin/events/${eventId}/unpublish`,
    pinEvent: (eventId: number) => `${HOST}/api/v1/admin/events/${eventId}/pin`,
    unpinEvent: (eventId: number) => `${HOST}/api/v1/admin/events/${eventId}/unpin`,
    uploadEventImage: `${HOST}/api/v1/admin/events/upload-image`,
  },
};
